import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Empty, Row, Segmented, Space, Statistic,
  Switch, Table, Tag, Typography, message
} from 'antd';
import {
  ReloadOutlined, PrinterOutlined, DownloadOutlined,
  RiseOutlined, FallOutlined, MinusCircleOutlined
} from '@ant-design/icons';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { accountReportsApi } from './accountReportsApi';
import { formatINR, shortDate } from './accountReportsConstants';
import { safeGroups, groupLabel, groupSubtitle } from './reportGroupingHelpers';
// Stage 6B.9H — friendly error / empty message helpers.
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Profit & Loss page (Stage 6B.7E).
//
// Read-only income statement: income on top, expenses below, net
// result badge. Built on posted, non-cancelled, non-deleted
// accounting_voucher_lines via the /reports/profit-loss endpoint.
// Magnitudes are operator-positive (the backend flips signs).
//
// Filters: from-to date range, includeZero. Print + CSV; NO PDF
// / Jasper integration (deliberate hold per Stage 6B.7E spec).
// ─────────────────────────────────────────────────────────────────

function csvSafe(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, sections, columns) {
  const lines = [];
  lines.push(columns.map((c) => csvSafe(c.title)).join(','));
  for (const section of sections) {
    lines.push('');
    lines.push(csvSafe(section.title));
    for (const r of section.rows) {
      lines.push(columns.map((c) =>
        csvSafe(typeof c.value === 'function' ? c.value(r) : r[c.dataIndex])
      ).join(','));
    }
    lines.push(csvSafe(`Total ${section.title}`) + ',,,'
      + ',' + csvSafe(section.total));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Stage 6B.9G — grouped P&L section. Renders one Card per group with
// its accounts inside; the section total tally matches the legacy
// summary.totalIncome / totalExpenses.
function PlGroupedSection({ title, icon, groups, total, columns, emptyText }) {
  const { Text } = Typography;
  if (!groups || groups.length === 0) {
    return (
      <Card size="small" title={<span>{icon}{title}</span>} style={{ marginBottom: 12 }}>
        <Empty description={emptyText} />
      </Card>
    );
  }
  return (
    <Card size="small" title={<span>{icon}{title}</span>} style={{ marginBottom: 12 }}>
      {groups.map((g) => (
        <Card
          key={`${g.accountSubtype || ''}::${g.accountGroup || ''}`}
          size="small"
          type="inner"
          style={{ marginBottom: 8 }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'baseline' }}>
              <span>
                <Text strong>{groupLabel(g)}</Text>
                {groupSubtitle(g) && groupSubtitle(g) !== groupLabel(g) ? (
                  <Text type="secondary" style={{ fontSize: 11.5, marginLeft: 6 }}>
                    {groupSubtitle(g)}
                  </Text>
                ) : null}
              </span>
              <Text strong>₹ {formatINR(g.total)}</Text>
            </div>
          }
        >
          <Table
            size="small"
            rowKey="accountId"
            columns={columns}
            dataSource={g.accounts}
            pagination={false}
          />
        </Card>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    padding: '6px 10px', borderTop: '1px solid #e2e8f0' }}>
        <Text strong>Total {title}</Text>
        <Text strong>₹ {formatINR(total)}</Text>
      </div>
    </Card>
  );
}

export default function ProfitLossPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Stage 6B.9H — preserve the last failure so the page can render an
  // Alert + Retry button instead of just flashing a toast.
  const [loadError, setLoadError] = useState(null);
  const [range, setRange] = useState(null);   // [moment, moment] or null
  const [includeZero, setIncludeZero] = useState(false);
  // Stage 6B.9G — view toggle between Flat (legacy) and Grouped.
  const [view, setView] = useState('flat');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (range && range[0] && range[1]) {
        params.fromDate = range[0].format('YYYY-MM-DD');
        params.toDate   = range[1].format('YYYY-MM-DD');
      }
      if (includeZero) params.includeZero = 'true';
      const out = await accountReportsApi.profitLoss(params);
      setData(out);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'profit_loss'));
    } finally { setLoading(false); }
  }, [range, includeZero]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const incomeRows  = data?.incomeRows  || [];
  const expenseRows = data?.expenseRows || [];
  // Stage 6B.9G — grouped sections.
  const incomeGroups   = useMemo(() => safeGroups(data?.income),   [data]);
  const expenseGroups  = useMemo(() => safeGroups(data?.expenses), [data]);
  const groupedAvailable = !!data?.meta?.groupedViewAvailable
    && (incomeGroups.length + expenseGroups.length > 0);
  const summary     = data?.summary || {
    totalIncome: 0, totalExpenses: 0, netProfitOrLoss: 0,
    resultType: 'breakeven', netMagnitude: 0
  };
  const meta = data?.meta || {};

  const columns = useMemo(() => ([
    { title: 'Code', dataIndex: 'accountCode', key: 'accountCode', width: 110,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Account Name', dataIndex: 'accountName', key: 'accountName' },
    { title: 'Group', dataIndex: 'accountGroup', key: 'accountGroup', width: 160,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Amount (₹)', dataIndex: 'amount', key: 'amount', width: 160, align: 'right',
      render: (v) => (
        <span style={{ color: v < 0 ? '#cf1322' : undefined,
                       fontWeight: v < 0 ? 600 : undefined }}>
          {formatINR(v)}
        </span>
      ) }
  ]), []);

  const csvColumns = useMemo(() => ([
    { title: 'Code',         dataIndex: 'accountCode' },
    { title: 'Account Name', dataIndex: 'accountName' },
    { title: 'Group',        dataIndex: 'accountGroup' },
    { title: 'Type',         dataIndex: 'accountType' },
    { title: 'Amount',       dataIndex: 'amount' }
  ]), []);

  // Operator-friendly badge: ↑ profit / ↓ loss / — breakeven.
  const resultBadge = useMemo(() => {
    if (summary.resultType === 'profit') {
      return (
        <Tag style={{ background: 'transparent', borderColor: '#52c41a',
                      color: '#1f7a25', fontWeight: 600, fontSize: 13 }}>
          <RiseOutlined /> Net Profit ₹{formatINR(summary.netMagnitude)}
        </Tag>
      );
    }
    if (summary.resultType === 'loss') {
      return (
        <Tag style={{ background: 'transparent', borderColor: '#cf1322',
                      color: '#991b1b', fontWeight: 600, fontSize: 13 }}>
          <FallOutlined /> Net Loss ₹{formatINR(summary.netMagnitude)}
        </Tag>
      );
    }
    return (
      <Tag style={{ background: 'transparent', borderColor: '#94a3b8',
                    color: '#64748b', fontWeight: 600, fontSize: 13 }}>
        <MinusCircleOutlined /> Breakeven
      </Tag>
    );
  }, [summary.resultType, summary.netMagnitude]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <div style={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', marginBottom: 4 }}>
          <Title level={4} style={{ margin: 0 }}>Profit &amp; Loss</Title>
          <Space>
            <Button size="small" icon={<ReloadOutlined />}
              onClick={fetchReport} loading={loading}>Refresh</Button>
            <Button size="small" icon={<PrinterOutlined />}
              onClick={() => window.print()}>Print</Button>
            <Button size="small" icon={<DownloadOutlined />}
              disabled={!incomeRows.length && !expenseRows.length}
              onClick={() => downloadCsv('profit-loss.csv', [
                { title: 'Income',   rows: incomeRows,  total: summary.totalIncome   },
                { title: 'Expenses', rows: expenseRows, total: summary.totalExpenses }
              ], csvColumns)}>
              Export CSV
            </Button>
          </Space>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          Income statement for the selected date range. Posted vouchers only; magnitudes are operator-positive.
        </div>

        {loadError ? (
          <Alert
            type="error" showIcon style={{ marginBottom: 10 }}
            message="Could not load Profit & Loss"
            description={friendlyAccountsErrorMessage(loadError, 'profit_loss')}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport}>
                Retry
              </Button>
            }
          />
        ) : null}

        {/* Header cards */}
        <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Total Income (₹)" value={summary.totalIncome}
                precision={2} valueStyle={{ color: '#1f7a25' }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Total Expenses (₹)" value={summary.totalExpenses}
                precision={2} valueStyle={{ color: '#b65b00' }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Net Profit / Loss (₹)"
                value={summary.netMagnitude} precision={2}
                valueStyle={{
                  color: summary.resultType === 'profit' ? '#1f7a25'
                       : summary.resultType === 'loss'   ? '#cf1322'
                       : '#64748b'
                }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <div style={{ fontSize: 11.5, color: '#64748b',
                            textTransform: 'uppercase', letterSpacing: 0.2,
                            fontWeight: 500 }}>Result</div>
              <div style={{ marginTop: 8 }}>{resultBadge}</div>
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>
                {meta.fromDate ? `${shortDate(meta.fromDate)} → ${shortDate(meta.toDate) || 'today'}` : 'All posted history'}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
          <Space wrap>
            {/* Stage 6B.9G — view toggle. */}
            <Space>
              <Text>View:</Text>
              <Segmented
                size="small"
                value={view}
                onChange={setView}
                options={[
                  { label: 'Flat',    value: 'flat' },
                  { label: 'Grouped', value: 'grouped', disabled: !groupedAvailable }
                ]}
              />
            </Space>
            <Space>
              <Text>Date range:</Text>
              <RangePicker size="small" value={range} onChange={setRange}
                allowClear format="YYYY-MM-DD" />
            </Space>
            <Space>
              <Text>Include zero rows:</Text>
              <Switch size="small" checked={includeZero} onChange={setIncludeZero} />
            </Space>
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              Excludes cancelled / draft / soft-deleted vouchers.
              Magnitudes are operator-positive; outlier rows (negative
              magnitude) flag misposted entries for review.
            </Text>
          </Space>
        </Card>

        {/* Stage 6B.9G — Grouped view renders Income/Expenses via the
            secondary grouping; flat view keeps the legacy layout. */}
        {view === 'grouped' && groupedAvailable ? (
          <>
            <PlGroupedSection
              title="Income"
              icon={<RiseOutlined style={{ color: '#1f7a25', marginRight: 6 }} />}
              groups={incomeGroups}
              total={summary.totalIncome}
              columns={columns}
              emptyText="No income postings in the selected range"
            />
            <PlGroupedSection
              title="Expenses"
              icon={<FallOutlined style={{ color: '#b65b00', marginRight: 6 }} />}
              groups={expenseGroups}
              total={summary.totalExpenses}
              columns={columns}
              emptyText="No expense postings in the selected range"
            />
          </>
        ) : null}

        {/* Income section — flat view */}
        {view === 'flat' || !groupedAvailable ? (
        <>
        <Card size="small" title={<span><RiseOutlined style={{ color: '#1f7a25', marginRight: 6 }} />Income</span>}
              style={{ marginBottom: 12 }}>
          <Table
            size="small"
            rowKey="accountId"
            loading={loading}
            columns={columns}
            dataSource={incomeRows}
            pagination={false}
            locale={{ emptyText: <Empty description="No income postings in the selected range" /> }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Total Income</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>₹ {formatINR(summary.totalIncome)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>

        {/* Expense section */}
        <Card size="small" title={<span><FallOutlined style={{ color: '#b65b00', marginRight: 6 }} />Expenses</span>}>
          <Table
            size="small"
            rowKey="accountId"
            loading={loading}
            columns={columns}
            dataSource={expenseRows}
            pagination={false}
            locale={{ emptyText: <Empty description="No expense postings in the selected range" /> }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Total Expenses</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>₹ {formatINR(summary.totalExpenses)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>
        </>
        ) : null}

        {/* Footer result strip */}
        <div style={{ marginTop: 12, padding: '10px 14px',
                      background: summary.resultType === 'profit' ? '#f0fdf4'
                                : summary.resultType === 'loss'   ? '#fef2f2'
                                : '#f8fafc',
                      border: `1px solid ${summary.resultType === 'profit' ? '#bbf7d0'
                                         : summary.resultType === 'loss'   ? '#fecaca'
                                         : '#e2e8f0'}`,
                      borderRadius: 4, display: 'flex',
                      justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text style={{ fontSize: 13, color: '#475569' }}>
              Income ₹{formatINR(summary.totalIncome)} − Expenses ₹{formatINR(summary.totalExpenses)} =
            </Text>{' '}
            {resultBadge}
          </div>
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            Basis: {meta.basis || 'posted_voucher_lines'} · No PDF/Jasper export by design.
          </Text>
        </div>
      </div>
    </ErpMasterShell>
  );
}
